import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { ArrowLeft, CreditCard, Edit3, ShoppingCart } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { database } from '@/db';
import { Customer } from '@/db/models/Customers';
import { Invoice, Payment } from '@/db/models/Billing';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import { useCartStore } from '@/store/useCartStore';
import { computeStatementTotals, isStatementVisible } from '@/customers/statementTotals';
import { recordCustomerPayment, type CustomerPaymentMethod } from '@/customers/customerLedger';
import { RBACGate, CUSTOMER_EDIT, CUSTOMER_STATEMENT } from '@/rbac';
import { CustomerForm } from './CustomerForm';

export function CustomerStatementScreen() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [paymentsByInvoice, setPaymentsByInvoice] = useState<Record<string, Payment[]>>({});

  const customers = useObservable<Customer[]>(
    () => customerId
      ? database.get<Customer>('customers').query(Q.where('id', customerId), Q.where('is_deleted', false)).observe()
      : null,
    [customerId],
    [],
  );
  const customer = customers[0] ?? null;

  const invoices = useObservable<Invoice[]>(
    () => customerId
      ? database.get<Invoice>('invoices').query(
        Q.where('customer_id', customerId),
        Q.where('is_deleted', false),
        Q.sortBy('created_at', Q.desc),
      ).observeWithColumns(['paid_amount', 'due_amount', 'payment_status'])
      : null,
    [customerId],
    [],
  );

  useEffect(() => {
    let alive = true;
    async function load() {
      const next: Record<string, Payment[]> = {};
      for (const invoice of invoices) {
        next[invoice.id] = await database.get<Payment>('payments').query(
          Q.where('invoice_id', invoice.id),
          Q.where('is_deleted', false),
          Q.sortBy('received_at', Q.desc),
        ).fetch();
      }
      if (alive) setPaymentsByInvoice(next);
    }
    void load();
    return () => { alive = false; };
  }, [invoices]);

  // Sums used to run blind across every row: a return added to purchases, a
  // refund counted as the customer paying, and a voided unpaid sale stayed in
  // outstanding dues. computeStatementTotals is the model the server reconciles
  // balances with, so this screen and the authoritative balance agree.
  const totals = useMemo(() => computeStatementTotals(invoices), [invoices]);
  const visibleInvoices = useMemo(() => invoices.filter(isStatementVisible), [invoices]);

  if (!customerId) return <div className="text-body text-fg-secondary">Customer not found.</div>;
  if (!customer) return <div className="text-body text-fg-secondary">Loading customer...</div>;

  function startSale() {
    useCartStore.getState().setCustomer({
      id: customer.id,
      name: customer.name,
      phone: customer.phone,
      gstin: customer.gstin,
      state: customer.state,
    });
    navigate('/billing');
  }

  return (
    <div className="flex h-full flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={() => navigate('/customers')}
            className="mb-3 inline-flex items-center gap-1.5 text-body text-fg-secondary hover:text-fg-primary"
          >
            <ArrowLeft size={14} /> Customers
          </button>
          <h1 className="text-display text-fg-primary">{customer.name || 'Unnamed customer'}</h1>
          <p className="mt-1 text-body text-fg-secondary">
            {[customer.phone, customer.email, customer.gstin].filter(Boolean).join(' · ') || 'No contact details'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RBACGate requires={CUSTOMER_EDIT}>
            <Button variant="secondary" onClick={() => setEditOpen(true)}><Edit3 size={14} /> Edit</Button>
          </RBACGate>
          <Button variant="secondary" onClick={startSale}><ShoppingCart size={14} /> New sale</Button>
          <RBACGate requires={CUSTOMER_STATEMENT}>
            <Button onClick={() => setPayOpen(true)} disabled={totals.due <= 0}><CreditCard size={14} /> Collect due</Button>
          </RBACGate>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Metric label="Total purchases" value={formatCurrency(totals.purchased)} />
        <Metric label="Paid" value={formatCurrency(totals.paid)} />
        <Metric label="Outstanding" value={formatCurrency(totals.due)} tone={totals.due > 0 ? 'warning' : 'success'} />
      </div>

      <div className="flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
        {invoices.length === 0 ? (
          <div className="flex h-full items-center justify-center text-body text-fg-tertiary">No invoices for this customer.</div>
        ) : (
          <table className="w-full text-body">
            <thead className="sticky top-0 bg-bg-surface">
              <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                <th className="px-4 py-2.5 text-left">Invoice</th>
                <th className="px-4 py-2.5 text-right">Total</th>
                <th className="px-4 py-2.5 text-right">Paid</th>
                <th className="px-4 py-2.5 text-right">Due</th>
                <th className="px-4 py-2.5 text-left">Payments</th>
              </tr>
            </thead>
            <tbody>
              {visibleInvoices.map((invoice) => (
                <tr key={invoice.id} className="border-b border-stroke-hairline/5 hover:bg-bg-subtle">
                  <td className="px-4 py-3">
                    <button className="font-medium text-accent-ink hover:underline" onClick={() => navigate(`/invoices/${invoice.id}`)}>
                      {invoice.invoiceNumber}
                    </button>
                    <div className="text-label text-fg-tertiary">{invoice.createdAt.toLocaleString()}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(invoice.totalAmount)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(invoice.paidAmount)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={cn('tabular-nums', invoice.dueAmount > 0 ? 'text-warning-ink' : 'text-success-ink')}>
                      {formatCurrency(Math.max(0, invoice.dueAmount))}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-fg-secondary">
                    {(paymentsByInvoice[invoice.id] ?? []).length === 0
                      ? 'No payment'
                      : (paymentsByInvoice[invoice.id] ?? []).map((p) => `${p.method} ${formatCurrency(p.amount)}`).join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent title="Edit customer" className="w-[720px]">
          <CustomerForm
            storeId={customer.storeId}
            customerId={customer.id}
            onSaved={() => setEditOpen(false)}
            onCancel={() => setEditOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent title="Collect customer due">
          <PaymentForm customerId={customer.id} maxAmount={totals.due} onDone={() => setPayOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: 'warning' | 'success' }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface p-4">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', tone === 'warning' ? 'text-warning-ink' : tone === 'success' ? 'text-success-ink' : 'text-fg-primary')}>
        {value}
      </div>
    </div>
  );
}

function PaymentForm({ customerId, maxAmount, onDone }: { customerId: string; maxAmount: number; onDone: () => void }) {
  const [amount, setAmount] = useState(maxAmount.toFixed(2));
  const [method, setMethod] = useState<CustomerPaymentMethod>('CASH');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));

  async function submit() {
    setSaving(true);
    setError(null);
    const result = await recordCustomerPayment({
      customerId,
      amount: Number(amount),
      method,
      reference,
      notes,
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.message ?? 'Failed to record payment.');
      return;
    }
    notifySuccess(`Payment of ${formatCurrency(Number(amount))} recorded.`);
    onDone();
  }

  return (
    <div className="space-y-4 p-5">
      <div className="rounded-md bg-bg-subtle px-3 py-2 text-body text-fg-secondary">
        Outstanding: <span className="font-medium text-fg-primary tabular-nums">{formatCurrency(maxAmount)}</span>
      </div>
      <label className="block">
        <span className="mb-1 block text-label uppercase text-fg-tertiary">Amount</span>
        <Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </label>
      <div>
        <span className="mb-1 block text-label uppercase text-fg-tertiary">Method</span>
        <div className="flex flex-wrap gap-2">
          {(['CASH', 'UPI', 'CARD', 'BANK_TRANSFER', 'CHEQUE'] as CustomerPaymentMethod[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMethod(m)}
              className={cn('rounded-md px-3 py-1.5 text-body', method === m ? 'btn-brand border border-white/10' : 'btn-brand-soft border border-ink-500/25')}
            >
              {m.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="mb-1 block text-label uppercase text-fg-tertiary">Reference</span>
        <Input value={reference} onChange={(e) => setReference(e.target.value)} />
      </label>
      <label className="block">
        <span className="mb-1 block text-label uppercase text-fg-tertiary">Notes</span>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving || !(Number(amount) > 0)}>{saving ? 'Saving...' : 'Record payment'}</Button>
      </div>
    </div>
  );
}
